import type { GameState, LeftFieldChoice, LeftFieldEvent, Resources } from '../game/types'
import { availableLeftFieldChoices, LEFT_FIELD_CONTINGENCY_CHOICE_ID, leftFieldChoiceAvailability } from '../game/leftField'

interface LeftFieldScreenProps {
  state: GameState
  onChoose: (choiceId: string) => void
  onDelegate: () => void
  onReturn: () => void
}

const labels: Record<keyof Resources, string> = {
  cash: 'Cash', research: 'Research', development: 'Development', institutional: 'Institution',
}

const humanise = (value: string) => value.replaceAll('-', ' ').replace(/\b\w/g, letter => letter.toUpperCase())

function ResourceDeltas({ delta, empty = 'No immediate resource change' }: { delta: Partial<Resources>; empty?: string }) {
  const entries = (Object.entries(delta) as Array<[keyof Resources, number]>).filter(([, value]) => value !== 0)
  if (!entries.length) return <span className="left-field-no-cost" role="group" aria-label="Immediate resource changes">{empty}</span>
  return <div className="left-field-resource-chips" role="group" aria-label="Immediate resource changes">{entries.map(([resource, value]) => (
    <span key={resource} className={value < 0 ? 'cost' : 'gain'}>{resource === 'cash'
      ? `Cash ${value > 0 ? '+' : '−'}$${Math.abs(value * 1000).toLocaleString()}k`
      : `${labels[resource]} ${value > 0 ? '+' : ''}${value}`}</span>
  ))}</div>
}

function ChoiceCard({ state, choice, onChoose }: { state: GameState; choice: LeftFieldChoice; onChoose: (id: string) => void }) {
  const availability = leftFieldChoiceAvailability(state, choice)
  const unavailableReasonId = `left-field-unavailable-${choice.id.replace(/[^a-zA-Z0-9_-]/g, '-')}`
  return <article className={`left-field-choice ${availability.available ? '' : 'unavailable'}`}>
    <div>
      <h2>{choice.label}</h2>
      <p>{choice.description}</p>
    </div>
    <ResourceDeltas delta={choice.resourceDelta} />
    <p className="left-field-uncertainty">Outcome uncertain — staff can assess the risk, but no result is guaranteed.</p>
    {availability.available
      ? <span className="left-field-availability available">Available</span>
      : <div id={unavailableReasonId} className="left-field-availability unavailable"><strong>Unavailable</strong>{availability.reasons.map(reason => <span key={reason}>{reason}</span>)}</div>}
    <button type="button" className="primary-action" disabled={!availability.available} aria-describedby={availability.available ? undefined : unavailableReasonId} onClick={() => onChoose(choice.id)}>Choose {choice.label}</button>
  </article>
}

function Outcome({ event, onReturn }: { event: LeftFieldEvent; onReturn: () => void }) {
  const choice = event.choices?.find(item => item.id === event.chosenChoiceId)
  const outcome = event.resolvedOutcomes?.[0]
  const delayed = event.pendingDelayedEffects ?? []
  return <div className="screen left-field-screen resolved-left-field">
    <div className="left-field-hero resolved">
      <span className="eyebrow">Out of Left Field · Resolved</span>
      <h1>{event.title}</h1>
      <p>{event.resolutionPath === 'delegated' ? 'Staff resolved this interruption.' : 'Coach resolved this interruption.'}</p>
    </div>
    <section className="panel left-field-outcome">
      <span className="left-field-resolution-path">Resolved by {event.resolutionPath === 'delegated' ? 'Staff' : 'Coach'}</span>
      <h2>{choice?.label ?? 'Decision recorded'}</h2>
      <p className="left-field-outcome-description">{outcome?.description ?? 'The matter has been closed.'}</p>
      <div className="left-field-consequence-grid">
        <div><strong>Immediate decision</strong><ResourceDeltas delta={choice?.resourceDelta ?? {}} /></div>
        <div><strong>Actual outcome</strong><ResourceDeltas delta={outcome?.resourceDelta ?? {}} empty="No additional resource change" /></div>
      </div>
      <div className="left-field-delayed">
        <strong>What carries forward</strong>
        {delayed.length ? delayed.map(effect => <p key={effect.id ?? effect.description}>{effect.description} · due in {effect.rounds} round{effect.rounds === 1 ? '' : 's'}</p>) : <p>No delayed consequence was scheduled.</p>}
        {event.followUpScheduled && <p>A related matter may return in a future week.</p>}
      </div>
    </section>
    <div className="sticky-actions"><span>The required interruption is resolved.</span><button type="button" className="primary-action" onClick={onReturn}>Return to Week</button></div>
  </div>
}

export function LeftFieldScreen({ state, onChoose, onDelegate, onReturn }: LeftFieldScreenProps) {
  const pending = state.leftField.currentEvent
  const latest = [...state.leftField.eventHistory].reverse().find(event => event.season === (state.season?.season ?? 1) && event.round === state.round)
  if (!pending) return latest ? <Outcome event={latest} onReturn={onReturn} /> : <div className="screen"><button type="button" className="back-link" onClick={onReturn}>Return to Week</button><p>No interruption is awaiting a decision.</p></div>

  const availableChoices = availableLeftFieldChoices(state, pending)
  return <div className="screen left-field-screen">
    <button type="button" className="back-link" aria-label="Back to Week" onClick={onReturn}>Back to Week</button>
    <div className="left-field-hero">
      <div className="left-field-meta"><span>{humanise(pending.archetype ?? 'football-operations')}</span><strong>{humanise(pending.severity ?? 'moderate')} severity</strong></div>
      <span className="eyebrow">Out of Left Field · Required interruption</span>
      <h1>{pending.title}</h1>
      <p>{pending.narrative}</p>
    </div>
    <section aria-labelledby="left-field-decisions">
      <div className="left-field-heading"><div><span className="eyebrow">Decision required</span><h2 id="left-field-decisions">Choose the club response</h2></div><p>Consequences are deterministic once selected, but exact outcome chances stay with staff.</p></div>
      <div className="left-field-choice-grid">{(pending.choices ?? []).map(choice => <ChoiceCard key={choice.id} state={state} choice={choice} onChoose={onChoose} />)}</div>
      {availableChoices.length === 0 && <div className="panel left-field-contingency" role="alert">
        <div><strong>No authored response is currently affordable or eligible.</strong><p>Use the standard club contingency to close the matter without spending resources. Match day will not remain blocked.</p></div>
        <button type="button" className="primary-action" onClick={() => onChoose(LEFT_FIELD_CONTINGENCY_CHOICE_ID)}>Use standard club contingency</button>
      </div>}
    </section>
    <section className="panel left-field-delegate">
      <div><span className="eyebrow">Fast weekly loop</span><h2>Delegate to Staff</h2><p>Football staff will choose an available response using the club's standing institutional policy. The resulting consequence still applies.</p></div>
      <button type="button" className="text-button" aria-label="Delegate to Staff" onClick={onDelegate}>Delegate to Staff</button>
    </section>
  </div>
}
